import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BrainCircuit, Globe, Zap, ListChecks,
  ShieldCheck, X, Save, RotateCcw, Server, Cloud // 👈 ضفنا Server و Cloud هنا
} from 'lucide-react';

const AISettingsModal = ({ isOpen, onClose, onSave, currentSettings, orgId, ai_provider, api_key }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  // حالة الإعدادات المحلية داخل المودال
  const [settings, setSettings] = useState({
    org_id: orgId,
    enable_predictive: false,
    target_language: 'en',
    confidence_threshold: 85,
    auto_create_tasks: true,
    max_tasks_count: 5,
    ai_provider: ai_provider, // 🎯 الحقل الجديد
    api_key: api_key           // 🎯 الحقل الجديد
  });

  // تحديث البيانات لو القادمة من الأب (Parent) تغيرت
  useEffect(() => {
    // 🎯 التعديل هنا: نحدث الـ state فقط لو currentSettings موجودة فعلاً والـ Modal مفتوح
    if (isOpen && currentSettings) {
      console.log(currentSettings);
      setSettings({
        ...currentSettings,
        org_id: orgId, // نضمن بقاء الـ orgId
        // نضمن تحويل أي قيمة (t/f) لـ Boolean حقيقي لـ React
        enable_predictive: !!currentSettings.enable_predictive,
        auto_create_tasks: !!currentSettings.auto_create_tasks,
        ai_provider: ai_provider || 'LOCAL', // 🎯 استرجاع المزود
        api_key: api_key || ''               // 🎯 استرجاع المفتاح
      });
    }
  }, [currentSettings, isOpen, orgId]);

  if (!isOpen) return null;

  const languages = [
    { code: 'ar', name: 'العربية' },
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'Français' },
    { code: 'es', name: 'Español' },
    { code: 'de', name: 'Deutsch' },
    { code: 'it', name: 'Italiano' },
    { code: 'ja', name: '日本語' },
    { code: 'ko', name: '한국어' },
    { code: 'ru', name: 'Русский' },
    { code: 'zh', name: '中文' },
    { code: 'ur', name: 'اُردُو' }
  ];

  return (
    <div className={`fixed inset-0 bg-slate-900/70 backdrop-blur-md z-[999] flex items-center justify-center p-4 transition-all ${isRTL ? 'font-arabic' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-surface-card w-full max-w-2xl rounded-3xl shadow-2xl border border-line overflow-hidden animate-in zoom-in-95 duration-300">

        {/* Header - الهيدر الاحترافي */}
        <div className="bg-primary-dark p-6 text-ink-inverse flex justify-between items-center relative">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/30 rounded-2xl backdrop-blur-sm border border-ink-inverse/10">
              <BrainCircuit size={28} className="text-ink-inverse/70 animate-pulse" />
            </div>
            <div>
              <h2 className="text-xl font-black tracking-tight">{t('ai_settings.title')}</h2>
              <p className="text-ink-inverse/70 text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">
                {t('ai_settings.sub_title')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-ink-inverse/10 rounded-full transition-colors text-ink-inverse/70 hover:text-ink-inverse"
          >
            <X size={24}/>
          </button>
        </div>

        <div className="p-8 space-y-8 max-h-[75vh] overflow-y-auto custom-scrollbar"> {/* 🎯 ضفنا scroll عشان المحتوى زاد */}

          {/* ================================================================= */}
          {/* 🎯 [الجزء المحقون] سيكشن اختيار محرك الذكاء الاصطناعي (Local vs Nvidia) */}
          {/* ================================================================= */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-primary-dark mb-2">
              <Server size={18} />
              <h3 className="font-black uppercase tracking-widest text-sm">{t('ai_settings.engineProviderTitle')}</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* خيار اللوكال */}
              <div
                onClick={() => setSettings({...settings, ai_provider: 'LOCAL'})}
                className={`cursor-pointer border-2 p-4 rounded-xl flex flex-col gap-2 transition-all ${settings.ai_provider === 'LOCAL' ? 'border-primary bg-primary-soft/50 shadow-md' : 'border-line hover:border-primary/50 bg-surface-card'}`}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2 text-primary font-black uppercase"><Server size={16}/> {t('ai_settings.localEngine')}</div>
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${settings.ai_provider === 'LOCAL' ? 'border-primary' : 'border-line'}`}>
                    {settings.ai_provider === 'LOCAL' && <div className="w-2 h-2 bg-primary rounded-full"/>}
                  </div>
                </div>
                <p className="text-[10px] text-ink-secondary font-bold leading-relaxed">{t('ai_settings.localEngineDesc')}</p>
              </div>

              {/* خيار الكلاود (Nvidia) */}
              <div
                onClick={() => setSettings({...settings, ai_provider: 'NVIDIA'})}
                className={`cursor-pointer border-2 p-4 rounded-xl flex flex-col gap-2 transition-all ${settings.ai_provider === 'NVIDIA' ? 'border-status-success bg-status-success-bg/50 shadow-md' : 'border-line hover:border-status-success/50 bg-surface-card'}`}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2 text-status-success font-black uppercase"><Cloud size={16}/> {t('ai_settings.groqCloud')}</div>
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${settings.ai_provider === 'NVIDIA' ? 'border-status-success' : 'border-line'}`}>
                    {settings.ai_provider === 'NVIDIA' && <div className="w-2 h-2 bg-status-success rounded-full"/>}
                  </div>
                </div>
                <p className="text-[10px] text-ink-secondary font-bold leading-relaxed">{t('ai_settings.groqCloudDesc')}</p>
              </div>
            </div>

            {/* إدخال مفتاح الـ API لو اختار NVIDIA */}
            {settings.ai_provider === 'NVIDIA' && (
              <div className="animate-in slide-in-from-top-2 mt-4 space-y-2">
                <label className="text-[11px] font-black text-ink-secondary uppercase flex items-center gap-2 tracking-widest">
                  <ShieldCheck size={14} className="text-status-success" /> {t('ai_settings.groqApiKey')}
                </label>
                <input
                  type="password"
                  placeholder="groq-..."
                  value={settings.api_key || ''}
                  onChange={(e) => setSettings({...settings, api_key: e.target.value})}
                  className="w-full bg-surface-panel border-2 border-line-subtle p-3.5 rounded-xl font-bold text-sm focus:border-status-success outline-none transition-all"
                />
                <p className="text-[9px] text-ink-secondary mt-1 italic">{t('ai_settings.groqApiKeyHint')}</p>
              </div>
            )}
          </div>

          <hr className="border-line-subtle" />
          {/* ================================================================= */}

          {/* Activation Section - تفعيل الصيانة التنبؤية */}
          <div className={`flex items-center justify-between p-5 rounded-2xl border-2 transition-all ${settings.enable_predictive ? 'bg-primary-soft border-primary/30 shadow-inner' : 'bg-surface-panel border-line-subtle'}`}>
            <div className="flex items-center gap-4">
              <div className={`p-2 rounded-lg ${settings.enable_predictive ? 'bg-primary text-ink-inverse' : 'bg-line text-ink-secondary'}`}>
                <Zap size={20} />
              </div>
              <div>
                <p className="font-black text-ink text-sm">{t('ai_settings.enable_label')}</p>
                <p className="text-[10px] text-ink-secondary font-bold uppercase italic tracking-wider">
                  {t('ai_settings.enable_sub')}
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer scale-110">
              <input
                type="checkbox"
                checked={Boolean(settings.enable_predictive)}
                onChange={(e) => setSettings({...settings, enable_predictive: e.target.checked})}
                className="sr-only peer"
              />
              <div className="w-12 h-6 bg-line peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-surface-card after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-surface-card after:border-line after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

            {/* Language - اختيار اللغة */}
            <div className="space-y-3">
              <label className="text-[11px] font-black text-ink-secondary uppercase flex items-center gap-2 tracking-widest">
                <Globe size={14} className="text-primary" /> {t('ai_settings.target_lang')}
              </label>
              <div className="relative">
                <select
                  value={settings.target_language}
                  onChange={(e) => setSettings({...settings, target_language: e.target.value})}
                  className="w-full bg-surface-panel border-2 border-line-subtle p-3.5 rounded-xl font-bold text-sm outline-none focus:border-primary appearance-none cursor-pointer transition-all"
                >
                  {languages.map(lang => (
                    <option key={lang.code} value={lang.code}>{lang.name}</option>
                  ))}
                </select>
                <div className={`absolute top-1/2 -translate-y-1/2 pointer-events-none ${isRTL ? 'left-4' : 'right-4'}`}>
                  <Globe size={16} className="text-ink-secondary" />
                </div>
              </div>
            </div>

            {/* Threshold - حد الثقة */}
            <div className="space-y-3">
              <label className="text-[11px] font-black text-ink-secondary uppercase flex items-center gap-2 tracking-widest">
                <ShieldCheck size={14} className="text-status-success" /> {t('ai_settings.threshold')}
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="50" max="100"
                  value={settings.confidence_threshold}
                  onChange={(e) => setSettings({...settings, confidence_threshold: parseInt(e.target.value)})}
                  className="w-full bg-surface-panel border-2 border-line-subtle p-3.5 rounded-xl font-bold text-sm focus:border-primary outline-none transition-all"
                />
                <span className={`absolute top-1/2 -translate-y-1/2 font-black text-ink-secondary ${isRTL ? 'left-4' : 'right-4'}`}>%</span>
              </div>
            </div>

            {/* Auto Task Generation - توليد المهام */}
            <div className="space-y-3">
              <label className="text-[11px] font-black text-ink-secondary uppercase flex items-center gap-2 tracking-widest">
                <ListChecks size={14} className="text-accent" /> {t('ai_settings.auto_tasks')}
              </label>
              <div className="flex items-center gap-4 p-3.5 bg-surface-panel rounded-xl border-2 border-line-subtle">
                <input
                  type="checkbox"
                  checked={settings.auto_create_tasks}
                  onChange={(e) => setSettings({...settings, auto_create_tasks: e.target.checked})}
                  className="w-5 h-5 text-primary border-line rounded focus:ring-primary transition-all cursor-pointer"
                />
                <span className="text-[11px] font-bold text-ink-secondary italic uppercase tracking-tighter">
                  {t('ai_settings.task_desc')}
                </span>
              </div>
            </div>

            {/* Max Tasks Count - أقصى عدد للمهام */}
            <div className="space-y-3">
              <label className="text-[11px] font-black text-ink-secondary uppercase tracking-widest">
                {t('ai_settings.max_tasks')}
              </label>
              <input
                type="number"
                min="1" max="15"
                value={settings.max_tasks_count}
                onChange={(e) => setSettings({...settings, max_tasks_count: parseInt(e.target.value)})}
                className="w-full bg-surface-panel border-2 border-line-subtle p-3.5 rounded-xl font-bold text-sm focus:border-primary outline-none transition-all"
              />
            </div>
          </div>
        </div>

        {/* Footer - أزرار التحكم */}
        <div className="p-6 bg-surface-panel flex justify-end gap-4 border-t border-line">
          <button
            onClick={onClose}
            className="flex items-center gap-2 px-6 py-2.5 text-xs font-black text-ink-secondary hover:text-ink uppercase italic transition-all"
          >
            <RotateCcw size={14}/> {t('common.cancel')}
          </button>
          <button
            onClick={() => onSave(settings)}
            className="flex items-center gap-2 bg-primary-dark text-ink-inverse px-10 py-3 rounded-xl shadow-xl font-black text-xs uppercase hover:bg-primary hover:-translate-y-1 transition-all active:scale-95"
          >
            <Save size={16}/> {t('common.save_settings')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AISettingsModal;
